import { useState, useEffect, useMemo } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { zodResolver } from '@hookform/resolvers/zod';
import {
  attendeeSchemaV2,
  speakerSchemaV2,
  TIPOS_ASISTENTE,
  TIPOS_PONENTE,
  RegistrationFormV2,
  HEALTH_CONDITION_OPTIONS,
  HEALTH_NONE_OPTION,
  HEALTH_OTHER_OPTION,
  BLOOD_TYPE_OPTIONS,
} from './schemas/registration-schema-v2';
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { FormSection } from "./form-section";
import { Users, User, MapPin, Building, Presentation, Globe, FileArchive, PlusCircle, Trash2, Calendar, Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "../src/api";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { useQuery } from "@tanstack/react-query";
import { getCountries, getStatesOfCountry, getCitiesOfState, type Country } from "../src/lib/location-api";
import { ConfirmationModal } from "./ui/confirmation-modal";
import { DeclaratoriaModal } from "./ui/declaratoria-modal";
import { CategoryPill } from "./CategoryPill";
import { PhoneInput } from "./ui/PhoneInput";
import { useIsMobile } from "./ui/use-mobile";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "./ui/drawer";
import { useSiaeValidation } from "../src/hooks/useSiaeValidation";
import { SiaeValidationSection } from "../src/components/siae/SiaeValidationSection";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";


type EventAxis = 'cientifico' | 'cultural' | 'innovacion';
const FREE_REGISTRATION_AXES = new Set<EventAxis>(['cultural', 'innovacion']);
const EARLY_PAYMENT_SYSWEB_ID = '202600210';
const GENERAL_PARTICIPATION_SYSWEB_ID = '202600207';
const SPECIAL_GENERAL_ACCESS_SYSWEB_IDS = new Set([EARLY_PAYMENT_SYSWEB_ID, GENERAL_PARTICIPATION_SYSWEB_ID]);
const BRACELET_CODE_REGEX = /^[1-9]\d{0,6}$/;
const BRACELET_REQUIRED_MESSAGE = 'El código de pulsera es obligatorio.';
const BRACELET_LEADING_ZERO_MESSAGE = 'El código de pulsera no debe tener cero inicial.';
const BRACELET_DUPLICATE_MESSAGE = 'El código de pulsera ya se encuentra registrado.';

const FIELD_LABELS: Record<string, string> = {
  tipoParticipante: 'Tipo de participante',
  participationMode: 'Modalidad',
  braceletCode: 'Código de pulsera',
  eventId: 'Evento',
  nombre: 'Nombre',
  apellidoPaterno: 'Apellido paterno',
  apellidoMaterno: 'Apellido materno',
  correo: 'Correo',
  telefono: 'Teléfono',
  curp: 'CURP',
  matricula: 'Matrícula',
  archivo: 'Archivo de ponencia',
};

function normalizeVisibleRegistrationMessage(message?: unknown): string {
  if (typeof message !== 'string' || !message.trim()) {
    return 'Error al enviar el registro.';
  }

  const normalized = message.toLowerCase();

  if (normalized === 'required') {
    return 'Este campo es obligatorio.';
  }

  if (normalized.includes('invalid enum value') || normalized.includes('expected')) {
    return 'Selecciona una opción válida.';
  }

  if (normalized.includes('pulsera') && (normalized.includes('registrad') || normalized.includes('duplic'))) {
    return BRACELET_DUPLICATE_MESSAGE;
  }

  if (normalized.includes('pulsera') && (normalized.includes('cero inicial') || normalized.includes('formato'))) {
    return BRACELET_LEADING_ZERO_MESSAGE;
  }

  if (message.trim().startsWith('{') || message.trim().startsWith('[')) {
    return 'Revisa la información del formulario.';
  }

  return message;
}

function getFriendlyValidationDescription(errors: unknown): string | undefined {
  if (!Array.isArray(errors)) {
    return undefined;
  }

  const messages = errors
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return undefined;
      }

      const field = 'field' in item && typeof item.field === 'string' ? item.field : '';
      const message = 'message' in item ? normalizeVisibleRegistrationMessage(item.message) : 'Revisa este campo.';
      const label = FIELD_LABELS[field] || '';

      return label ? `${label}: ${message}` : message;
    })
    .filter(Boolean);

  return messages.length ? messages.join('\n') : undefined;
}

function isFreeRegistrationAxis(axis: EventAxis | string | null | undefined): boolean {
  return FREE_REGISTRATION_AXES.has(axis as EventAxis);
}

function isSpecialGeneralAccessEvent(syswebId: string | number | null | undefined): boolean {
  const normalizedSyswebId = syswebId == null ? '' : String(syswebId).trim();
  return SPECIAL_GENERAL_ACCESS_SYSWEB_IDS.has(normalizedSyswebId);
}

interface Event {
  id: string;
  name: string;
  slug: string;
  bannerUrl?: string;
  axis: EventAxis | string;
  syswebId?: string | null;
  speakerBraceletEnabled?: boolean;
}

interface RegistrationResult {
  id: string;
  nombre: string;
  folio: string;
  createdAt: string;
}

interface CurpVerificationResult {
  nombre: string;
  apellidoPaterno: string;
  apellidoMaterno: string;
  genero: string;
  edad: number;
}

type UnachParticipationMode = "OYENTE_UNACH" | "ASISTENTE";
type GeneralParticipationMode = "GENERAL_CON_KIT" | "GENERAL_SIN_KIT";
type SourceOfTruth = "CURP" | "SIAE" | null;

type RegistrationAvailabilityReason = "MANUAL" | "EXPIRED" | null;

interface RegistrationTypeAvailabilityItem {
  enabled: boolean;
  effectiveEnabled: boolean;
  reason: RegistrationAvailabilityReason;
  deadline: string | null;
}

interface RegistrationTypeAvailability {
  attendee: RegistrationTypeAvailabilityItem;
  speaker: RegistrationTypeAvailabilityItem;
}

interface PersonalDataSnapshot {
  nombre: string;
  apellidoPaterno: string;
  apellidoMaterno: string;
  genero: string;
  edad: number;
}

interface SiaeFormSource {
  nombreCompleto?: string;
  genero?: string;
  fechaNacimiento?: string;
}

function createDefaultRegistrationAvailabilityItem(): RegistrationTypeAvailabilityItem {
  return {
    enabled: true,
    effectiveEnabled: true,
    reason: null,
    deadline: null,
  };
}

function getAvailabilityHelperText(item: RegistrationTypeAvailabilityItem): string | null {
  if (item.reason === "EXPIRED") {
    return "La convocatoria ha finalizado";
  }

  if (!item.effectiveEnabled) {
    return "Deshabilitado temporalmente";
  }

  return null;
}

function calculateAgeFromSiaeDate(rawDate?: string): number {
  if (!rawDate) return 0;

  const [day, month, year] = rawDate.split("-").map((part) => Number(part));
  if (!day || !month || !year) return 0;

  const today = new Date();
  let age = today.getFullYear() - year;
  const hasNotHadBirthdayYet =
    today.getMonth() + 1 < month ||
    (today.getMonth() + 1 === month && today.getDate() < day);

  if (hasNotHadBirthdayYet) {
    age -= 1;
  }

  return age;
}

function mapSiaeToForm(data: SiaeFormSource): PersonalDataSnapshot {
  const parts = (data.nombreCompleto || "").trim().split(/\s+/).filter(Boolean);
  const genderMap: Record<string, string> = {
    F: "mujer",
    M: "hombre",
  };

  let nombre = "";
  let apellidoPaterno = "";
  let apellidoMaterno = "";

  if (parts.length >= 3) {
    nombre = parts.slice(0, -2).join(" ");
    apellidoPaterno = parts[parts.length - 2];
    apellidoMaterno = parts[parts.length - 1];
  } else if (parts.length === 2) {
    nombre = parts[0];
    apellidoPaterno = parts[1];
  } else {
    nombre = parts[0] || "";
  }

  return {
    nombre,
    apellidoPaterno,
    apellidoMaterno,
    genero: genderMap[(data.genero || "").trim().toUpperCase()] || "",
    edad: calculateAgeFromSiaeDate(data.fechaNacimiento),
  };
}

function getNextHealthConditions(currentConditions: string[], condition: string, checked: boolean): string[] {
  if (condition === HEALTH_NONE_OPTION) {
    return checked ? [HEALTH_NONE_OPTION] : [];
  }

  const filteredConditions = currentConditions.filter((item) => item !== HEALTH_NONE_OPTION && item !== condition);
  return checked ? [...filteredConditions, condition] : filteredConditions;
}

export function RegistrationForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [csrfToken, setCsrfToken] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeclaratoriaOpen, setIsDeclaratoriaOpen] = useState(false);
  const [registrationData, setRegistrationData] = useState<RegistrationResult | null>(null);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [isEventDrawerOpen, setIsEventDrawerOpen] = useState(false);
  const [isVerifyingCurp, setIsVerifyingCurp] = useState(false);
  const [isCurpVerified, setIsCurpVerified] = useState(false);
  const [verifiedCurp, setVerifiedCurp] = useState('');
  const [isUnachStudent, setIsUnachStudent] = useState<boolean | null>(null);
  const [isSiaeValidated, setIsSiaeValidated] = useState(false);
  const [verifiedMatricula, setVerifiedMatricula] = useState('');
  const [validatedStudentName, setValidatedStudentName] = useState<string | null>(null);
  const [sourceOfTruth, setSourceOfTruth] = useState<SourceOfTruth>(null);
  const [curpSnapshot, setCurpSnapshot] = useState<PersonalDataSnapshot | null>(null);
  const [showUnachSwitchConfirm, setShowUnachSwitchConfirm] = useState(false);
  const [hasBracelet, setHasBracelet] = useState(false);
  const [showScientificCurpBlock, setShowScientificCurpBlock] = useState(false);
  const [registrationTypeAvailability, setRegistrationTypeAvailability] = useState<RegistrationTypeAvailability>({
    attendee: createDefaultRegistrationAvailabilityItem(),
    speaker: createDefaultRegistrationAvailabilityItem(),
  });
  const isMobile = useIsMobile();
  const { validateMatricula, loading: isVerifyingSiae, error: siaeError, setError: setSiaeError } = useSiaeValidation();

  // --- Multi-Event State ---
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedAxis, setSelectedAxis] = useState<EventAxis | null>(null);

  // Fetch Events and CSRF
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [csrfResponse, eventsResponse] = await Promise.all([
          api.get('/csrf-token'),
          api.get('/events')
        ]);
        const publicSettingsResponse = await api.get('/public/settings').catch(() => ({ data: {} }));
        const publicSettings = publicSettingsResponse.data || {};
        const attendeeStatus = publicSettings.REGISTRATION_ATTENDEE_STATUS;
        const speakerStatus = publicSettings.REGISTRATION_SPEAKER_STATUS;
        setCsrfToken(csrfResponse.data.token);
        setEvents(eventsResponse.data || []);
        setRegistrationTypeAvailability({
          attendee:
            attendeeStatus && typeof attendeeStatus === 'object'
              ? {
                  enabled: Boolean((attendeeStatus as any).enabled),
                  effectiveEnabled: Boolean((attendeeStatus as any).effectiveEnabled),
                  reason: ((attendeeStatus as any).reason ?? null) as RegistrationAvailabilityReason,
                  deadline: typeof (attendeeStatus as any).deadline === 'string' ? (attendeeStatus as any).deadline : null,
                }
              : {
                  enabled:
                    typeof publicSettings.REGISTRATION_ATTENDEE_ENABLED === 'boolean'
                      ? publicSettings.REGISTRATION_ATTENDEE_ENABLED
                      : true,
                  effectiveEnabled:
                    typeof publicSettings.REGISTRATION_ATTENDEE_ENABLED === 'boolean'
                      ? publicSettings.REGISTRATION_ATTENDEE_ENABLED
                      : true,
                  reason: null,
                  deadline:
                    typeof publicSettings.REGISTRATION_ATTENDEE_DEADLINE === 'string'
                      ? publicSettings.REGISTRATION_ATTENDEE_DEADLINE
                      : null,
                },
          speaker:
            speakerStatus && typeof speakerStatus === 'object'
              ? {
                  enabled: Boolean((speakerStatus as any).enabled),
                  effectiveEnabled: Boolean((speakerStatus as any).effectiveEnabled),
                  reason: ((speakerStatus as any).reason ?? null) as RegistrationAvailabilityReason,
                  deadline: typeof (speakerStatus as any).deadline === 'string' ? (speakerStatus as any).deadline : null,
                }
              : {
                  enabled:
                    typeof publicSettings.REGISTRATION_SPEAKER_ENABLED === 'boolean'
                      ? publicSettings.REGISTRATION_SPEAKER_ENABLED
                      : true,
                  effectiveEnabled:
                    typeof publicSettings.REGISTRATION_SPEAKER_ENABLED === 'boolean'
                      ? publicSettings.REGISTRATION_SPEAKER_ENABLED
                      : true,
                  reason: null,
                  deadline:
                    typeof publicSettings.REGISTRATION_SPEAKER_DEADLINE === 'string'
                      ? publicSettings.REGISTRATION_SPEAKER_DEADLINE
                      : null,
                },
        });
      } catch (error) {
        console.error('Failed to fetch initial data', error);
        toast.error('Error de carga', { description: 'No se pudieron cargar los eventos disponibles.' });
      } finally {
        setIsLoadingInitial(false);
      }
    };
    fetchInitialData();
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    reset,
    setValue,
    setError,
  } = useForm<RegistrationFormV2>({
    resolver: (values, context, options) => {
      const schema = values.role === 'SPEAKER' ? speakerSchemaV2 : attendeeSchemaV2;
      return (zodResolver(schema as any) as any)(values, context, options);
    },
    mode: "onChange",
    shouldUnregister: true,
    defaultValues: {
      role: '',
      eventId: '',
      isExtranjero: false,
      curp: '',
      matricula: '',
      braceletCode: '',
      participationMode: undefined,
      nombre: '',
      apellidoPaterno: '',
      apellidoMaterno: '',
      genero: '',
      correo: '',
      telefono: '',
      edad: '' as any,
      pais: '',
      estado: '',
      ciudad: '',
      healthData: {
        conditions: [],
        detailsImportant: '',
        otherDetails: '',
        emergencyContactName: '',
        emergencyContactPhone: '',
        bloodType: undefined,
        medicalAuthorization: undefined,
      },
      institucion: '',
      tipoParticipante: '',
      comoSeEntero: '',
      aceptaTerminos: false,
      coautores: [],
      area: '',
      tituloContribucion: '',
      resumen: '',
    } as any,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "coautores" as any,
  });

  const selectedRole = watch("role");
  const otherSectionTitle = selectedRole === 'SPEAKER' ? '9. Otros' : '6. Otros';
  const selectedEventId = watch("eventId");
  const isExtranjero = watch("isExtranjero");
  const curpValue = watch("curp");
  const matriculaValue = watch("matricula");
  const braceletCodeValue = watch("braceletCode" as any) as string | undefined;
  const tipoParticipanteValue = watch("tipoParticipante");
  const healthConditions = watch("healthData.conditions") || [];
  const selectedEvent = events.find(e => e.id === selectedEventId);
  const isFreeAxisEvent = isFreeRegistrationAxis(selectedEvent?.axis);
  const isEarlyPaymentEvent = selectedEvent?.syswebId === EARLY_PAYMENT_SYSWEB_ID;
  const isSpecialGeneralAccessSelectedEvent = isSpecialGeneralAccessEvent(selectedEvent?.syswebId);
  const isSpeakerBraceletEnabled = selectedRole === 'SPEAKER' && selectedEvent?.speakerBraceletEnabled === true;
  const attendeeRegistrationAvailability = registrationTypeAvailability.attendee;
  const speakerRegistrationAvailability = registrationTypeAvailability.speaker;
  const isAttendeeRegistrationEnabled = attendeeRegistrationAvailability.effectiveEnabled;
  const isSpeakerRegistrationEnabled = speakerRegistrationAvailability.effectiveEnabled;
  const filteredEvents = useMemo(() => {
    if (!selectedAxis) return [];
    return events.filter((event) => event.axis === selectedAxis);
  }, [events, selectedAxis]);
  const isEventSelectDisabled = isLoadingInitial || !selectedAxis || filteredEvents.length === 0;

  const handleAxisChange = (axis: EventAxis) => {
    setSelectedAxis(axis);
    setValue("eventId", "");
    setValue("role", undefined as any);
    setValue("isExtranjero", false);
    setValue("curp", "");
    setValue("matricula", "");
    setValue("braceletCode" as any, "");
    setValue("participationMode", undefined as any);
    setValue("healthData.conditions", [] as any);
    resetHealthDependentFields();
    setValue("tipoParticipante", "");
    setIsCurpVerified(false);
    setVerifiedCurp('');
    setIsUnachStudent(null);
    setIsSiaeValidated(false);
    setVerifiedMatricula('');
    setValidatedStudentName(null);
    setSourceOfTruth(null);
    setCurpSnapshot(null);
    setShowUnachSwitchConfirm(false);
    setSiaeError(null);
    setHasBracelet(false);
    setShowScientificCurpBlock(false);
  };

  useEffect(() => {
    if (isFreeRegistrationAxis(selectedEvent?.axis) && selectedRole === 'SPEAKER') {
      setValue('role', undefined as any);
    }
    if (isSpecialGeneralAccessEvent(selectedEvent?.syswebId)) {
      if (isAttendeeRegistrationEnabled && selectedRole !== 'ATTENDEE') {
        setValue('role', 'ATTENDEE' as any, { shouldDirty: true, shouldValidate: true });
      } else if (!isAttendeeRegistrationEnabled && selectedRole === 'ATTENDEE') {
        setValue('role', undefined as any, { shouldDirty: true, shouldValidate: true });
      }
    }
  }, [selectedEventId, selectedEvent, selectedRole, isAttendeeRegistrationEnabled, setValue]);

  useEffect(() => {
    if (selectedRole === 'ATTENDEE' && !isAttendeeRegistrationEnabled) {
      setValue('role', undefined as any, { shouldDirty: true, shouldValidate: true });
    }
    if (selectedRole === 'SPEAKER' && !isSpeakerRegistrationEnabled) {
      setValue('role', undefined as any, { shouldDirty: true, shouldValidate: true });
    }
  }, [selectedRole, isAttendeeRegistrationEnabled, isSpeakerRegistrationEnabled, setValue]);

  useEffect(() => {
    if (selectedRole === 'SPEAKER' && selectedEvent && !selectedEvent.speakerBraceletEnabled) {
      setValue("braceletCode" as any, "", { shouldDirty: true, shouldValidate: true });
    }
  }, [selectedRole, selectedEvent, setValue]);

  useEffect(() => {
    if (!selectedRole) {
      setValue("isExtranjero", false);
      setValue("curp", "");
      setValue("matricula", "");
      setValue("braceletCode" as any, "");
      setValue("participationMode", undefined as any);
      setValue("healthData.conditions", [] as any);
      resetHealthDependentFields();
      setValue("tipoParticipante", "");
      setIsCurpVerified(false);
      setVerifiedCurp('');
      setIsUnachStudent(null);
      setIsSiaeValidated(false);
      setVerifiedMatricula('');
      setValidatedStudentName(null);
      setSourceOfTruth(null);
      setCurpSnapshot(null);
      setShowUnachSwitchConfirm(false);
      setSiaeError(null);
      setHasBracelet(false);
    }
  }, [selectedRole, setValue]);

  useEffect(() => {
    if (isExtranjero) {
      setIsCurpVerified(false);
      setVerifiedCurp('');
      setValue("curp", "");
      setIsUnachStudent(null);
      setIsSiaeValidated(false);
      setVerifiedMatricula('');
      setValidatedStudentName(null);
      setValue("matricula", "");
      setValue("participationMode", undefined as any);
      setSiaeError(null);
    }
  }, [isExtranjero, setValue, setSiaeError]);

  useEffect(() => {
    const normalizedCurp = typeof curpValue === 'string' ? curpValue.trim().toUpperCase() : '';
    if (!normalizedCurp || normalizedCurp !== verifiedCurp) {
      setIsCurpVerified(false);
      setIsUnachStudent(null);
      setIsSiaeValidated(false);
      setVerifiedMatricula('');
      setValidatedStudentName(null);
      setSourceOfTruth(null);
      setCurpSnapshot(null);
      setShowUnachSwitchConfirm(false);
      setValue("matricula", "");
      setValue("participationMode", undefined as any);
      setSiaeError(null);
    }
  }, [curpValue, verifiedCurp, setValue, setSiaeError]);

  useEffect(() => {
    const normalizedMatricula = typeof matriculaValue === 'string' ? matriculaValue.trim() : '';
    if (!normalizedMatricula || normalizedMatricula !== verifiedMatricula) {
      setIsSiaeValidated(false);
      if (normalizedMatricula !== verifiedMatricula) {
        setValidatedStudentName(null);
      }
    }
  }, [matriculaValue, verifiedMatricula]);


  // Location state management
  const [selectedCountryIso, setSelectedCountryIso] = useState<string>("");
  const [selectedStateIso, setSelectedStateIso] = useState<string>("");
  const [showManualCityInput, setShowManualCityInput] = useState(false);
  const [phoneCountryIso, setPhoneCountryIso] = useState<string>("");
  const [phoneTouchedManually, setPhoneTouchedManually] = useState(false);

  const { data: countries = [] } = useQuery<Country[]>({
    queryKey: ["countries"],
    queryFn: getCountries,
  });

  const { data: states, isLoading: isLoadingStates } = useQuery({
    queryKey: ["states", selectedCountryIso],
    queryFn: () => getStatesOfCountry(selectedCountryIso),
    enabled: !!selectedCountryIso,
  });

  const { data: cities, isLoading: isLoadingCities } = useQuery({
    queryKey: ["cities", selectedCountryIso, selectedStateIso],
    queryFn: () => getCitiesOfState(selectedCountryIso, selectedStateIso),
    enabled: !!selectedCountryIso && !!selectedStateIso,
  });

  const handleModalClose = () => {
    setIsModalOpen(false);
    setRegistrationData(null);
    setPhoneCountryIso("");
    setPhoneTouchedManually(false);
    setIsCurpVerified(false);
    setVerifiedCurp('');
    setIsUnachStudent(null);
    setIsSiaeValidated(false);
    setVerifiedMatricula('');
    setValidatedStudentName(null);
    setSourceOfTruth(null);
    setCurpSnapshot(null);
    setShowUnachSwitchConfirm(false);
    setSiaeError(null);
    reset();
  };

  const clearVerifiedPersonalData = () => {
    setValue("nombre", "");
    setValue("apellidoPaterno", "");
    setValue("apellidoMaterno", "");
    setValue("genero", "" as any);
    setValue("edad", "" as any);
  };

  const applyPersonalData = (data: PersonalDataSnapshot) => {
    setValue("nombre", data.nombre, { shouldDirty: true, shouldValidate: true });
    setValue("apellidoPaterno", data.apellidoPaterno, { shouldDirty: true, shouldValidate: true });
    setValue("apellidoMaterno", data.apellidoMaterno, { shouldDirty: true, shouldValidate: true });
    setValue("genero", data.genero as any, { shouldDirty: true, shouldValidate: true });
    setValue("edad", data.edad as any, { shouldDirty: true, shouldValidate: true });
  };

  const resetHealthDependentFields = () => {
    setValue("healthData.detailsImportant", "", { shouldDirty: true, shouldValidate: true });
    setValue("healthData.otherDetails", "", { shouldDirty: true, shouldValidate: true });
    setValue("healthData.emergencyContactName", "", { shouldDirty: true, shouldValidate: true });
    setValue("healthData.emergencyContactPhone", "", { shouldDirty: true, shouldValidate: true });
    setValue("healthData.bloodType", undefined as any, { shouldDirty: true, shouldValidate: true });
    setValue("healthData.medicalAuthorization", undefined as any, { shouldDirty: true, shouldValidate: true });
  };

  const resetCurpVerification = () => {
    setIsCurpVerified(false);
    setVerifiedCurp('');
    setSourceOfTruth(null);
    setCurpSnapshot(null);
    setValue("curp", "");
    clearVerifiedPersonalData();
  };

  const resetSiaeVerification = () => {
    setIsSiaeValidated(false);
    setVerifiedMatricula('');
    setValidatedStudentName(null);
    setSourceOfTruth(curpSnapshot ? "CURP" : null);
    setValue("matricula", "");
    setValue("participationMode", undefined as any);
    if (tipoParticipanteValue === "estudiante-unach") {
      setValue("tipoParticipante", "");
    }
    setSiaeError(null);
  };

  const handleConfirmUnachNo = () => {
    resetSiaeVerification();
    setIsUnachStudent(false);
    if (curpSnapshot) {
      applyPersonalData(curpSnapshot);
      setSourceOfTruth("CURP");
    } else {
      clearVerifiedPersonalData();
      setSourceOfTruth(null);
    }
    setShowUnachSwitchConfirm(false);
  };

  const handleHealthConditionToggle = (condition: string, checked: boolean) => {
    const nextConditions = getNextHealthConditions(healthConditions, condition, checked);
    setValue("healthData.conditions", nextConditions as any, { shouldDirty: true, shouldValidate: true });

    if (nextConditions.includes(HEALTH_NONE_OPTION)) {
      resetHealthDependentFields();
      return;
    }

    if (!nextConditions.includes(HEALTH_OTHER_OPTION)) {
      setValue("healthData.otherDetails", "", { shouldDirty: true, shouldValidate: true });
    }
  };

  const handleVerifyCurp = async () => {
    if (!csrfToken) {
      toast.error('Error de seguridad', { description: 'Falta token CSRF. Recargue la página.' });
      return;
    }

    const normalizedCurp = (curpValue || '').trim().toUpperCase();
    if (!normalizedCurp) {
      toast.error('CURP requerida', { description: 'Ingresa una CURP valida antes de consultar.' });
      return;
    }

    setIsVerifyingCurp(true);
    try {
      const response = await api.post<CurpVerificationResult>(
        '/registrations/verify-curp',
        { curp: normalizedCurp },
        {
          headers: {
            'x-csrf-token': csrfToken,
          },
        }
      );

      const nextCurpSnapshot: PersonalDataSnapshot = {
        nombre: response.data.nombre,
        apellidoPaterno: response.data.apellidoPaterno,
        apellidoMaterno: response.data.apellidoMaterno,
        genero: response.data.genero,
        edad: response.data.edad,
      };

      setValue("curp", normalizedCurp, { shouldDirty: true, shouldValidate: true });
      setCurpSnapshot(nextCurpSnapshot);
      if (sourceOfTruth !== "SIAE") {
        applyPersonalData(nextCurpSnapshot);
        setSourceOfTruth("CURP");
      }
      setVerifiedCurp(normalizedCurp);
      setIsCurpVerified(true);

      toast.success('CURP validada', { description: 'Los datos personales fueron autocompletados.' });
    } catch (error: any) {
      setIsCurpVerified(false);
      setVerifiedCurp('');
      clearVerifiedPersonalData();
      const message = error?.response?.data?.message || 'No fue posible validar la CURP.';
      toast.error('Validacion CURP fallida', { description: message });
    } finally {
      setIsVerifyingCurp(false);
    }
  };

  const handleVerifySiae = async () => {
    const normalizedMatricula = (matriculaValue || '').trim();
    if (!normalizedMatricula) {
      toast.error('Matricula requerida', { description: 'Ingresa una matricula valida antes de consultar.' });
      return;
    }

    try {
      const response = await validateMatricula(normalizedMatricula);
      if (!response.valid) {
        setIsSiaeValidated(false);
        setValidatedStudentName(null);
        return;
      }

      const mappedSiaeData = mapSiaeToForm({
        nombreCompleto: response.nombreCompleto,
        genero: response.genero,
        fechaNacimiento: response.fechaNacimiento,
      });

      setValue("matricula", normalizedMatricula, { shouldDirty: true, shouldValidate: true });
      setVerifiedMatricula(normalizedMatricula);
      setValidatedStudentName(response.nombreCompleto || null);
      setIsSiaeValidated(true);
      setValue("tipoParticipante", "estudiante-unach", { shouldDirty: true, shouldValidate: true });
      applyPersonalData(mappedSiaeData);
      setSourceOfTruth("SIAE");
      toast.success('Matricula validada', { description: 'Ya puedes seleccionar la modalidad UNACH.' });
    } catch (error: any) {
      setIsSiaeValidated(false);
      setValidatedStudentName(null);
      toast.error('Validacion SIAE fallida', { description: error?.message || 'No fue posible validar la matricula.' });
    }
  };

  const requiresUnachDecision = selectedRole === 'ATTENDEE' && !isExtranjero && !isFreeAxisEvent;
  const hasCompletedIdentityStep = isExtranjero || isCurpVerified;
  const hasCompletedUnachStep =
    !requiresUnachDecision ||
    (isUnachStudent !== null && (isUnachStudent === false || isSiaeValidated));
  const showHealthDetails = healthConditions.some((condition) => condition !== HEALTH_NONE_OPTION);
  const isOtherHealthConditionSelected = healthConditions.includes(HEALTH_OTHER_OPTION);

  const showPersonalSection = Boolean(
    selectedRole &&
    selectedEventId &&
    hasCompletedIdentityStep &&
    hasCompletedUnachStep
  );
  const lockVerifiedFields = !isExtranjero && sourceOfTruth !== null;
  const lockedFieldClassName = lockVerifiedFields ? "bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed" : "";
  const healthErrors = ((errors as any).healthData || {}) as Record<string, any>;

  useEffect(() => {
    if (selectedRole === 'ATTENDEE' && isFreeAxisEvent && hasCompletedIdentityStep) {
      setIsUnachStudent(false);
      resetSiaeVerification();
      setValue("participationMode", "GENERAL_SIN_KIT" as any, { shouldDirty: true, shouldValidate: true });
    }
  }, [selectedRole, isFreeAxisEvent, hasCompletedIdentityStep, setValue]);

  useEffect(() => {
    if (selectedRole === 'ATTENDEE' && isSpecialGeneralAccessSelectedEvent && isUnachStudent === false) {
      setValue("tipoParticipante", "publico-general", { shouldDirty: true, shouldValidate: true });
      setValue("participationMode", "GENERAL_SIN_KIT" as any, { shouldDirty: true, shouldValidate: true });
    }
  }, [selectedRole, isSpecialGeneralAccessSelectedEvent, isUnachStudent, setValue]);

  const onSubmit = async (data: RegistrationFormV2) => {
    if (!csrfToken) {
      toast.error('Error de seguridad', { description: 'Falta token CSRF. Recargue la página.' });
      return;
    }

    if (data.role === 'SPEAKER' && isSpecialGeneralAccessSelectedEvent) {
      toast.error('Registro no disponible', { description: 'Este evento solo permite registro como asistente.' });
      return;
    }

    const normalizedBraceletCode = (braceletCodeValue || '').trim();
    const isBraceletRequired =
      (data.role === 'SPEAKER' && selectedEvent?.speakerBraceletEnabled === true) ||
      (data.role === 'ATTENDEE' && isEarlyPaymentEvent) ||
      (data.role === 'ATTENDEE' && isFreeAxisEvent && hasBracelet);

    if (isBraceletRequired && !normalizedBraceletCode) {
      setError('braceletCode' as any, { type: 'manual', message: BRACELET_REQUIRED_MESSAGE });
      toast.error(BRACELET_REQUIRED_MESSAGE);
      return;
    }

    if (normalizedBraceletCode && !BRACELET_CODE_REGEX.test(normalizedBraceletCode)) {
      setError('braceletCode' as any, { type: 'manual', message: BRACELET_LEADING_ZERO_MESSAGE });
      toast.error(BRACELET_LEADING_ZERO_MESSAGE);
      return;
    }

    if (data.role === 'ATTENDEE' && !isFreeAxisEvent && isUnachStudent) {
      if (!isSiaeValidated) {
        toast.error('Validacion pendiente', { description: 'Debes validar tu matricula UNACH antes de continuar.' });
        return;
      }

      if (!isSpecialGeneralAccessSelectedEvent && !data.participationMode) {
        toast.error('Modalidad requerida', { description: 'Selecciona si deseas registrarte como asistente u oyente UNACH.' });
        return;
      }
    }

    if (data.role === 'ATTENDEE' && !isFreeAxisEvent && !isSpecialGeneralAccessSelectedEvent && !isExtranjero && isUnachStudent === false && !data.participationMode) {
      toast.error('Modalidad requerida', { description: 'Selecciona Kit y constancia o Constancia antes de continuar.' });
      return;
    }

    setIsSubmitting(true);
    const formData = new FormData();

    const normalizedParticipationMode =
      data.role === 'ATTENDEE' && isFreeAxisEvent
        ? 'GENERAL_SIN_KIT'
        : data.role === 'ATTENDEE' && isSpecialGeneralAccessSelectedEvent && isUnachStudent === false
          ? 'GENERAL_SIN_KIT'
          : data.participationMode === 'OYENTE_UNACH'
            ? 'OYENTE_UNACH'
            : data.participationMode === 'GENERAL_CON_KIT' || data.participationMode === 'GENERAL_SIN_KIT'
              ? data.participationMode
              : undefined;
    const normalizedTipoParticipante =
      data.role === 'ATTENDEE' && isExtranjero && !data.tipoParticipante
        ? 'publico-general'
        : data.tipoParticipante;

    Object.entries({
      ...data,
      participationMode: normalizedParticipationMode,
      tipoParticipante: normalizedTipoParticipante,
      braceletCode: normalizedBraceletCode,
    }).forEach(([key, value]) => {
      if (key === 'documento' && value instanceof FileList && value.length > 0) {
        formData.append('archivo', value[0]);
      } else if (key === 'coautores' && Array.isArray(value)) {
        formData.append(key, JSON.stringify(value));
      } else if (key === 'healthData' && value && typeof value === 'object') {
        formData.append(key, JSON.stringify(value));
      } else if (value !== undefined && value !== null && value !== '') {
        formData.append(key, String(value));
      }
    });

    const endpoint = data.role === 'SPEAKER'
      ? '/registrations/speaker'
      : '/registrations/attendee';

    try {
      const response = await api.post(
        endpoint,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'x-csrf-token': csrfToken,
          },
        }
      );

      if (response.status === 201) {
        setRegistrationData(response.data);
        setIsModalOpen(true);
      }
    } catch (error: any) {
      console.error("Error durante el registro:", error);
      const msg = normalizeVisibleRegistrationMessage(error?.response?.data?.message || "Error al enviar el registro.");
      if (error?.response?.status === 403) {
        toast.error('Registro no disponible', {
          description: msg || 'El periodo de registro ha finalizado.',
        });
        return;
      }
      if (typeof msg === 'string' && msg.includes('registro activo en el eje Cientifico')) {
        setShowScientificCurpBlock(true);
        return;
      }
      const subErrors = error?.response?.data?.errors;
      if (Array.isArray(subErrors)) {
        subErrors.forEach((item: any) => {
          const field = typeof item?.field === 'string' ? item.field : '';
          if (field && FIELD_LABELS[field]) {
            setError(field as any, {
              type: 'server',
              message: normalizeVisibleRegistrationMessage(item?.message),
            });
          }
        });
      }
      const friendlyDescription = getFriendlyValidationDescription(subErrors);
      const duplicateMatricula =
        typeof msg === 'string' &&
        msg.toLowerCase().includes('matricula') &&
        msg.toLowerCase().includes('registrada');
      toast.error(msg, {
        description: duplicateMatricula
          ? 'Esta matricula ya esta registrada en este evento.'
          : friendlyDescription
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onInvalidSubmit = (formErrors: typeof errors) => {
    const firstError = Object.values(formErrors)[0] as { message?: string } | undefined;
    toast.error('Revisa la informacion del formulario', {
      description: firstError?.message || 'Hay campos obligatorios pendientes o con formato invalido.',
    });
  };

  return (
    <>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* SECCIÓN INTRODUCCIÓN (Patrón Seguimiento/Login) */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center bg-[#0f2744] rounded-2xl p-4 shadow-xl border-2 border-[#c9a227]/30 mb-6 group transition-all hover:scale-105">
            <Users className="h-10 w-10 text-[#c9a227]" />
          </div>
          <h1 className="text-3xl font-black text-[#0f2744] uppercase tracking-tighter">
            Registro <span className="text-[#c9a227]">CIU 2026</span>
          </h1>
          <p className="text-gray-500 font-serif italic mt-2">
            Inicia tu proceso de participación
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit as any, onInvalidSubmit as any)} className="space-y-8">
          {/* SECCIÓN 0: EVENTO */}
          <FormSection title="Selecciona tu Evento" icon={Calendar}>
            <div className="space-y-2">
              <Label className="text-sm font-bold text-gray-700">Eje Temático *</Label>
              <div role="radiogroup" aria-label="Eje temático" className="flex flex-col sm:flex-row items-stretch gap-2 w-full">
                <CategoryPill
                  label="Científico"
                  isActive={selectedAxis === 'cientifico'}
                  onClick={() => handleAxisChange('cientifico')}
                />
                <CategoryPill
                  label="Cultural"
                  isActive={selectedAxis === 'cultural'}
                  onClick={() => handleAxisChange('cultural')}
                />
                <CategoryPill
                  label="Empresarial"
                  isActive={selectedAxis === 'innovacion'}
                  onClick={() => handleAxisChange('innovacion')}
                />
              </div>

              <div key={selectedAxis || 'sin-eje'} className="space-y-2 mt-4 animate-in fade-in duration-200">
                <Label className="text-sm font-bold text-gray-700">Evento *</Label>
                <Controller
                  name="eventId"
                  control={control}
                  render={({ field }) => (
                    isMobile ? (
                      <Drawer open={isEventDrawerOpen} onOpenChange={setIsEventDrawerOpen}>
                        <DrawerTrigger asChild>
                          <button
                            type="button"
                            disabled={isEventSelectDisabled}
                            className="border-input flex w-full items-center justify-between gap-2 rounded-md border bg-white px-3 py-3 text-left text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50 min-h-[60px]"
                          >
                            <span className="min-w-0 flex-1 pr-2">
                              {selectedEvent ? (
                                <span className="block font-medium line-clamp-1">{selectedEvent.name}</span>
                              ) : (
                                <span className="text-gray-500">
                                  {isLoadingInitial
                                    ? "Cargando eventos..."
                                    : !selectedAxis
                                      ? "Selecciona un eje temático"
                                      : filteredEvents.length === 0
                                        ? "No hay eventos disponibles para este eje"
                                        : "Selecciona un evento de la lista"}
                                </span>
                              )}
                            </span>
                            <ChevronDown className="h-4 w-4 text-gray-500 shrink-0" />
                          </button>
                        </DrawerTrigger>
                        <DrawerContent className="max-h-[85vh]">
                          <DrawerHeader>
                            <DrawerTitle className="text-lg font-semibold">Selecciona el evento</DrawerTitle>
                          </DrawerHeader>
                          <div className="px-4 pb-4 overflow-y-auto">
                            <div className="space-y-2">
                              {filteredEvents.map((event) => {
                                const isSelected = field.value === event.id;
                                return (
                                  <button
                                    key={event.id}
                                    type="button"
                                    aria-selected={isSelected}
                                    onClick={() => {
                                      field.onChange(event.id);
                                      setIsEventDrawerOpen(false);
                                    }}
                                    className={`w-full text-left rounded-lg px-3 py-4 border transition-colors min-h-[44px] ${isSelected
                                      ? 'bg-blue-50 border-blue-400'
                                      : 'bg-white border-gray-200 hover:bg-gray-50'
                                      }`}
                                  >
                                    <div className="flex items-start gap-2">
                                      <span className={`flex-1 text-base leading-snug whitespace-normal break-words ${isSelected ? 'text-blue-800 font-medium' : 'text-gray-800'}`}>
                                        {event.name}
                                      </span>
                                      {isSelected && <Check className="h-4 w-4 text-blue-700 mt-0.5 shrink-0" />}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </DrawerContent>
                      </Drawer>
                    ) : (
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || ""}
                        disabled={isEventSelectDisabled}
                      >
                        <SelectTrigger className="bg-white text-left h-auto py-3 min-h-[60px]">
                          <SelectValue
                            placeholder={
                              isLoadingInitial
                                ? "Cargando eventos..."
                                : !selectedAxis
                                  ? "Selecciona un eje temático"
                                  : filteredEvents.length === 0
                                    ? "No hay eventos disponibles para este eje"
                                    : "Selecciona un evento de la lista"
                            }
                          >
                            {selectedEvent ? (
                              <span className="flex items-start text-left w-full pr-4">
                                <span className="font-medium whitespace-normal break-words leading-snug">{selectedEvent.name}</span>
                              </span>
                            ) : undefined}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="max-h-[400px]">
                          {filteredEvents.map((event) => (
                            <SelectItem key={event.id} value={event.id} className="py-3 border-b last:border-0">
                              <span
                                title={event.name}
                                className="font-medium block text-sm sm:text-base whitespace-normal break-words leading-snug line-clamp-2"
                              >
                                {event.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )
                  )}
                />
                {selectedAxis && filteredEvents.length === 0 && !isLoadingInitial && (
                  <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded border border-amber-100">
                    No hay eventos disponibles para este eje.
                  </p>
                )}
              </div>
              {errors.eventId && <p className="text-red-500 text-sm">{errors.eventId.message}</p>}

              {selectedEvent && (
                <div className="mt-2 text-sm text-gray-600 bg-blue-50 p-3 rounded border border-blue-100 animate-in fade-in">
                  Has seleccionado: <span className="font-semibold text-blue-800">{selectedEvent.name}</span>
                </div>
              )}
            </div>
          </FormSection>

          {selectedEventId && (
            <div className="animate-in fade-in slide-in-from-top-4 duration-500 space-y-8">
              <FormSection title="¿Cómo deseas participar?" icon={Users}>
                <Controller
                  name="role"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      value={field.value || ''}
                      onValueChange={(val) => {
                        if (val === 'ATTENDEE' && !isAttendeeRegistrationEnabled) return;
                        if (val === 'SPEAKER' && !isSpeakerRegistrationEnabled) return;
                        field.onChange(val);
                      }}
                      className="grid grid-cols-1 md:grid-cols-2 gap-4"
                    >
                      <div className={`flex items-center space-x-2 p-4 border rounded-lg transition-colors ${!isAttendeeRegistrationEnabled ? 'cursor-not-allowed border-gray-200 bg-gray-100 opacity-70' : field.value === 'ATTENDEE' ? 'cursor-pointer bg-blue-50 border-blue-500' : 'cursor-pointer hover:bg-gray-50'}`}>
                        <RadioGroupItem value="ATTENDEE" id="role-attendee" disabled={!isAttendeeRegistrationEnabled} />
                        <Label htmlFor="role-attendee" className={`${isAttendeeRegistrationEnabled ? 'cursor-pointer' : 'cursor-not-allowed'} flex-1`}>
                          <div className="font-medium">Asistente</div>
                          <div className="text-sm text-gray-500">Participar como oyente</div>
                          {!isAttendeeRegistrationEnabled && (
                            <div className="mt-1 text-xs font-semibold text-gray-600">{getAvailabilityHelperText(attendeeRegistrationAvailability)}</div>
                          )}
                        </Label>
                      </div>

                      {selectedEvent?.axis === 'cientifico' && !isSpecialGeneralAccessSelectedEvent && (
                        <div className={`flex items-center space-x-2 p-4 border rounded-lg transition-colors ${!isSpeakerRegistrationEnabled ? 'cursor-not-allowed border-gray-200 bg-gray-100 opacity-70' : field.value === 'SPEAKER' ? 'cursor-pointer bg-green-50 border-green-500' : 'cursor-pointer hover:bg-gray-50'}`}>
                          <RadioGroupItem value="SPEAKER" id="role-speaker" disabled={!isSpeakerRegistrationEnabled} />
                          <Label htmlFor="role-speaker" className={`${isSpeakerRegistrationEnabled ? 'cursor-pointer' : 'cursor-not-allowed'} flex-1`}>
                            <div className="font-medium">Ponente</div>
                            <div className="text-sm text-gray-500">Presentar trabajo</div>
                            {!isSpeakerRegistrationEnabled && (
                              <div className="mt-1 text-xs font-semibold text-gray-600">{getAvailabilityHelperText(speakerRegistrationAvailability)}</div>
                            )}
                          </Label>
                        </div>
                      )}
                    </RadioGroup>
                  )}
                />
                {isFreeAxisEvent && (
                  <p className="text-sm text-amber-600 mt-2 bg-amber-50 p-2 rounded">
                    Nota: Este evento ({selectedEvent?.axis}) no acepta registro de ponentes por este medio.
                  </p>
                )}
                {isSpecialGeneralAccessSelectedEvent && (
                  <p className="text-sm text-amber-600 mt-2 bg-amber-50 p-2 rounded">
                    Nota: Este evento especial solo permite registro como asistente.
                  </p>
                )}
                {errors.role && <p className="text-red-500 text-sm mt-2">{errors.role.message}</p>}
              </FormSection>
            </div>
          )}

          {selectedRole && selectedEventId && (
            <div className="animate-in fade-in slide-in-from-top-4 duration-500 space-y-8">
              <FormSection title="1. Nacionalidad y validación CURP" icon={User}>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-bold text-gray-700">¿Tu nacionalidad es mexicana? *</Label>
                    <Controller
                      name="isExtranjero"
                      control={control}
                      render={({ field }) => (
                        <RadioGroup
                          value={field.value ? 'extranjero' : 'nacional'}
                          onValueChange={(value) => {
                            const nextIsExtranjero = value === 'extranjero';
                            field.onChange(nextIsExtranjero);
                            if (nextIsExtranjero) {
                              clearVerifiedPersonalData();
                            }
                          }}
                          className="grid grid-cols-1 md:grid-cols-2 gap-4"
                        >
                          <div className={`flex items-center space-x-2 p-4 border rounded-lg cursor-pointer transition-colors ${!field.value ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'}`}>
                            <RadioGroupItem value="nacional" id="nacionalidad-nacional" />
                            <Label htmlFor="nacionalidad-nacional" className="cursor-pointer flex-1">
                              <div className="font-medium">Sí, soy mexicano(a)</div>
                              <div className="text-sm text-gray-500">Validaré mis datos con CURP</div>
                            </Label>
                          </div>
                          <div className={`flex items-center space-x-2 p-4 border rounded-lg cursor-pointer transition-colors ${field.value ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'}`}>
                            <RadioGroupItem value="extranjero" id="nacionalidad-extranjero" />
                            <Label htmlFor="nacionalidad-extranjero" className="cursor-pointer flex-1">
                              <div className="font-medium">No, soy extranjero(a)</div>
                              <div className="text-sm text-gray-500">Capturaré mis datos manualmente</div>
                            </Label>
                          </div>
                        </RadioGroup>
                      )}
                    />
                  </div>

                  {!isExtranjero && (
                    <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-4 space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
                        <div className="space-y-2">
                          <Label className="text-sm font-bold text-gray-700">CURP *</Label>
                          <Input
                            {...register("curp")}
                            placeholder="Ingresa tu CURP"
                            className="uppercase"
                            maxLength={18}
                            readOnly={isCurpVerified}
                          />
                          {errors.curp && <p className="text-red-500 text-sm">{errors.curp.message as string}</p>}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            onClick={handleVerifyCurp}
                            disabled={isVerifyingCurp || !curpValue || isCurpVerified}
                            className="bg-[#0f2744] hover:bg-[#142d4d] text-white"
                          >
                            {isVerifyingCurp ? "Validando..." : "Validar CURP"}
                          </Button>
                          {isCurpVerified && (
                            <Button type="button" variant="outline" onClick={resetCurpVerification}>
                              Cambiar CURP
                            </Button>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-gray-600">
                        Si no conoces tu CURP, puedes consultarla en{" "}
                        <a
                          href="https://www.gob.mx/curp/"
                          target="_blank"
                          rel="noreferrer"
                          className="font-medium text-blue-700 underline"
                        >
                          gob.mx/curp
                        </a>.
                      </p>
                      {!isCurpVerified && (
                        <p className="text-sm text-amber-700">
                          Debes validar la CURP para desbloquear la captura de datos personales.
                        </p>
                      )}
                    </div>
                  )}

                  {!isExtranjero && isSpeakerBraceletEnabled && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-2">
                      <Label className="text-sm font-bold text-gray-700">Código de pulsera *</Label>
                      <Input
                        {...register("braceletCode" as any)}
                        inputMode="numeric"
                        maxLength={7}
                      />
                      <p className="text-sm text-gray-600">
                        El código de pulsera no debe tener cero inicial.
                      </p>
                      {(errors as any).braceletCode && <p className="text-red-500 text-sm">{(errors as any).braceletCode.message}</p>}
                    </div>
                  )}

                  {selectedRole === 'ATTENDEE' && (isFreeAxisEvent || isEarlyPaymentEvent) && hasCompletedIdentityStep && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-4">
                      {isEarlyPaymentEvent ? (
                        <div className="space-y-2">
                          <Label className="text-sm font-bold text-gray-700">Código de pulsera *</Label>
                          <Input
                            {...register("braceletCode" as any)}
                            inputMode="numeric"
                            maxLength={7}
                          />
                          <p className="text-sm text-gray-500">El código de pulsera no debe tener cero inicial.</p>
                          {(errors as any).braceletCode && <p className="text-red-500 text-sm">{(errors as any).braceletCode.message}</p>}
                        </div>
                      ) : (
                        <>
                          <div className="space-y-2">
                            <Label className="text-sm font-bold text-gray-700">¿Cuentas con pulsera de acceso?</Label>
                            <RadioGroup
                              value={hasBracelet ? 'si' : 'no'}
                              onValueChange={(value) => {
                                const nextHasBracelet = value === 'si';
                                setHasBracelet(nextHasBracelet);
                                if (!nextHasBracelet) {
                                  setValue("braceletCode" as any, "", { shouldDirty: true, shouldValidate: true });
                                }
                              }}
                              className="grid grid-cols-1 md:grid-cols-2 gap-4"
                            >
                              <div className={`flex items-center space-x-2 p-4 border rounded-lg cursor-pointer transition-colors ${hasBracelet ? 'bg-blue-50 border-blue-500' : 'hover:bg-white'}`}>
                                <RadioGroupItem value="si" id="pulsera-si" />
                                <Label htmlFor="pulsera-si" className="cursor-pointer flex-1">
                                  <div className="font-medium">Sí</div>
                                  <div className="text-sm text-gray-500">Tengo un código de pulsera.</div>
                                </Label>
                              </div>
                              <div className={`flex items-center space-x-2 p-4 border rounded-lg cursor-pointer transition-colors ${!hasBracelet ? 'bg-blue-50 border-blue-500' : 'hover:bg-white'}`}>
                                <RadioGroupItem value="no" id="pulsera-no" />
                                <Label htmlFor="pulsera-no" className="cursor-pointer flex-1">
                                  <div className="font-medium">No</div>
                                  <div className="text-sm text-gray-500">Continuar sin pulsera registrada.</div>
                                </Label>
                              </div>
                            </RadioGroup>
                          </div>

                          {hasBracelet && (
                            <div className="space-y-2">
                              <Label className="text-sm font-bold text-gray-700">Código de pulsera *</Label>
                              <Input
                                {...register("braceletCode" as any)}
                                inputMode="numeric"
                                maxLength={7}
                              />
                              <p className="text-sm text-gray-500">El código de pulsera no debe tener cero inicial.</p>
                              {(errors as any).braceletCode && <p className="text-red-500 text-sm">{(errors as any).braceletCode.message}</p>}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {!isExtranjero && selectedRole === 'ATTENDEE' && isCurpVerified && !isFreeAxisEvent && (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-bold text-gray-700">¿Eres estudiante UNACH?</Label>
                        <Controller
                          name="tipoParticipante"
                          control={control}
                          render={() => (
                            <RadioGroup
                              value={
                                isUnachStudent === null
                                  ? ""
                                  : isUnachStudent
                                    ? "si"
                                    : "no"
                              }
                              onValueChange={(value) => {
                                const isStudent = value === "si";
                                if (isStudent) {
                                  setIsUnachStudent(true);
                                  setValue("participationMode", undefined as any, { shouldDirty: true, shouldValidate: true });
                                  setValue("tipoParticipante", "estudiante-unach", { shouldDirty: true, shouldValidate: true });
                                } else {
                                  if (sourceOfTruth === "SIAE") {
                                    setShowUnachSwitchConfirm(true);
                                    return;
                                  }

                                  resetSiaeVerification();
                                  setIsUnachStudent(false);
                                  if (curpSnapshot) {
                                    applyPersonalData(curpSnapshot);
                                    setSourceOfTruth("CURP");
                                  }
                                }
                              }}
                              className="grid grid-cols-1 md:grid-cols-2 gap-4"
                            >
                              <div className={`flex items-center space-x-2 p-4 border rounded-lg cursor-pointer transition-colors ${isUnachStudent === true ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'}`}>
                                <RadioGroupItem value="si" id="estudiante-unach-si" />
                                <Label htmlFor="estudiante-unach-si" className="cursor-pointer flex-1">
                                  <div className="font-medium">Sí</div>
                                  <div className="text-sm text-gray-500">Validaré mi matrícula institucional</div>
                                </Label>
                              </div>
                              <div className={`flex items-center space-x-2 p-4 border rounded-lg cursor-pointer transition-colors ${isUnachStudent === false ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'}`}>
                                <RadioGroupItem value="no" id="estudiante-unach-no" />
                                <Label htmlFor="estudiante-unach-no" className="cursor-pointer flex-1">
                                  <div className="font-medium">No</div>
                                  <div className="text-sm text-gray-500">Continuaré con el flujo normal de asistente</div>
                                </Label>
                              </div>
                            </RadioGroup>
                          )}
                        />
                        <p className="text-sm text-gray-500">
                          Esta decisión puede cambiarse en cualquier momento antes de enviar el registro.
                        </p>
                      </div>

                      {isUnachStudent === true && (
                        <>
                          <SiaeValidationSection
                            matricula={matriculaValue || ""}
                            onMatriculaChange={(value) => {
                              setValue("matricula", value, { shouldDirty: true, shouldValidate: true });
                              if (value !== verifiedMatricula) {
                                setValue("participationMode", undefined as any, { shouldDirty: true, shouldValidate: true });
                              }
                            }}
                            onValidate={handleVerifySiae}
                            loading={isVerifyingSiae}
                            error={siaeError}
                            verifiedName={validatedStudentName}
                            isValidated={isSiaeValidated}
                            onReset={resetSiaeVerification}
                            fieldError={errors.matricula?.message as string | undefined}
                          />

                          {isSiaeValidated && !isSpecialGeneralAccessSelectedEvent && (
                            <div className="space-y-2">
                              <Label className="text-sm font-bold text-gray-700">Modalidad UNACH *</Label>
                              <Controller
                                name="participationMode"
                                control={control}
                                render={({ field }) => (
                                  <RadioGroup
                                    value={field.value || ""}
                                    onValueChange={(value) => field.onChange(value as UnachParticipationMode)}
                                    className="grid grid-cols-1 md:grid-cols-2 gap-4"
                                  >
                                    <div className={`flex items-center space-x-2 p-4 border rounded-lg cursor-pointer transition-colors ${field.value === 'ASISTENTE' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'}`}>
                                      <RadioGroupItem value="ASISTENTE" id="modalidad-asistente-unach" />
                                      <Label htmlFor="modalidad-asistente-unach" className="cursor-pointer flex-1">
                                        <div className="font-medium">Asistente UNACH</div>
                                        <div className="text-sm text-gray-500">Con kit y constancia. Sigue el flujo normal de pago.</div>
                                      </Label>
                                    </div>
                                    <div className={`flex items-center space-x-2 p-4 border rounded-lg cursor-pointer transition-colors ${field.value === 'OYENTE_UNACH' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'}`}>
                                      <RadioGroupItem value="OYENTE_UNACH" id="modalidad-oyente-unach" />
                                      <Label htmlFor="modalidad-oyente-unach" className="cursor-pointer flex-1">
                                        <div className="font-medium">Oyente UNACH</div>
                                        <div className="text-sm text-gray-500">Sin kit ni constancia. Registro gratuito validado por SIAE.</div>
                                      </Label>
                                    </div>
                                  </RadioGroup>
                                )}
                              />
                              {errors.participationMode && <p className="text-red-500 text-sm">{errors.participationMode.message as string}</p>}
                            </div>
                          )}
                        </>
                      )}

                      {isUnachStudent === false && !isSpecialGeneralAccessSelectedEvent && (
                        <div className="space-y-2">
                          <Label className="text-sm font-bold text-gray-700">Modalidad de pago *</Label>
                          <Controller
                            name="participationMode"
                            control={control}
                            render={({ field }) => (
                              <RadioGroup
                                value={field.value || ""}
                                onValueChange={(value) => field.onChange(value as GeneralParticipationMode)}
                                className="grid grid-cols-1 md:grid-cols-2 gap-4"
                              >
                                <div className={`flex items-center space-x-2 p-4 border rounded-lg cursor-pointer transition-colors ${field.value === 'GENERAL_CON_KIT' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'}`}>
                                  <RadioGroupItem value="GENERAL_CON_KIT" id="modalidad-general-con-kit" />
                                  <Label htmlFor="modalidad-general-con-kit" className="cursor-pointer flex-1">
                                    <div className="font-medium">Kit y constancia</div>
                                    <div className="text-sm text-gray-500">Asistente general con kit de participante y constancia.</div>
                                  </Label>
                                </div>
                                <div className={`flex items-center space-x-2 p-4 border rounded-lg cursor-pointer transition-colors ${field.value === 'GENERAL_SIN_KIT' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'}`}>
                                  <RadioGroupItem value="GENERAL_SIN_KIT" id="modalidad-general-sin-kit" />
                                  <Label htmlFor="modalidad-general-sin-kit" className="cursor-pointer flex-1">
                                    <div className="font-medium">Constancia</div>
                                    <div className="text-sm text-gray-500">Asistente general con constancia.</div>
                                  </Label>
                                </div>
                              </RadioGroup>
                            )}
                          />
                          {errors.participationMode && <p className="text-red-500 text-sm">{errors.participationMode.message as string}</p>}
                        </div>
                      )}
                    </div>
                  )}



                  {isExtranjero && (
                    <div className="space-y-4">
                      <p className="rounded-lg border border-emerald-100 bg-emerald-50 p-3 text-sm text-emerald-700">
                        Participante extranjero: la captura de datos personales se habilita manualmente.
                      </p>

                      {isSpeakerBraceletEnabled && (
                        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-2">
                          <Label className="text-sm font-bold text-gray-700">Código de pulsera *</Label>
                          <Input
                            {...register("braceletCode" as any)}
                            inputMode="numeric"
                            maxLength={7}
                          />
                          <p className="text-sm text-gray-600">
                            El código de pulsera no debe tener cero inicial.
                          </p>
                          {(errors as any).braceletCode && <p className="text-red-500 text-sm">{(errors as any).braceletCode.message}</p>}
                        </div>
                      )}

                      {selectedRole === 'ATTENDEE' && !isFreeAxisEvent && !isSpecialGeneralAccessSelectedEvent && (
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-2">
                          <Label className="text-sm font-bold text-gray-700">Modalidad de pago</Label>
                          <Controller
                            name="participationMode"
                            control={control}
                            render={({ field }) => (
                              <RadioGroup
                                value={field.value || ""}
                                onValueChange={(value) => field.onChange(value as GeneralParticipationMode)}
                                className="grid grid-cols-1 md:grid-cols-2 gap-4"
                              >
                                <div className={`flex items-center space-x-2 p-4 border rounded-lg cursor-pointer transition-colors ${field.value === 'GENERAL_CON_KIT' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'}`}>
                                  <RadioGroupItem value="GENERAL_CON_KIT" id="modalidad-general-con-kit-extranjero" />
                                  <Label htmlFor="modalidad-general-con-kit-extranjero" className="cursor-pointer flex-1">
                                    <div className="font-medium">Kit y constancia</div>
                                    <div className="text-sm text-gray-500">Asistente general con kit de participante y constancia.</div>
                                  </Label>
                                </div>
                                <div className={`flex items-center space-x-2 p-4 border rounded-lg cursor-pointer transition-colors ${field.value === 'GENERAL_SIN_KIT' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'}`}>
                                  <RadioGroupItem value="GENERAL_SIN_KIT" id="modalidad-general-sin-kit-extranjero" />
                                  <Label htmlFor="modalidad-general-sin-kit-extranjero" className="cursor-pointer flex-1">
                                    <div className="font-medium">Constancia</div>
                                    <div className="text-sm text-gray-500">Asistente general con constancia.</div>
                                  </Label>
                                </div>
                              </RadioGroup>
                            )}
                          />
                          {errors.participationMode && <p className="text-red-500 text-sm">{errors.participationMode.message as string}</p>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </FormSection>

              {showPersonalSection && (
                <FormSection title="2. Datos Personales" icon={User}>
                  {sourceOfTruth && (
                    <div className={`mb-4 rounded-lg border p-3 text-sm font-medium ${sourceOfTruth === "SIAE"
                      ? "border-indigo-200 bg-indigo-50 text-indigo-800"
                      : "border-emerald-200 bg-emerald-50 text-emerald-800"
                      }`}>
                      {sourceOfTruth === "SIAE"
                        ? "Datos validados con matricula UNACH (SIAE)"
                        : "Datos validados con CURP"}
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-bold text-gray-700">Nombre(s) *</Label>
                      <Input {...register("nombre")} readOnly={lockVerifiedFields} className={lockedFieldClassName} />
                      {errors.nombre && <p className="text-red-500 text-sm">{errors.nombre.message}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-bold text-gray-700">Apellido Paterno *</Label>
                      <Input {...register("apellidoPaterno")} readOnly={lockVerifiedFields} className={lockedFieldClassName} />
                      {errors.apellidoPaterno && <p className="text-red-500 text-sm">{errors.apellidoPaterno.message}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-bold text-gray-700">Apellido Materno *</Label>
                      <Input {...register("apellidoMaterno")} readOnly={lockVerifiedFields} className={lockedFieldClassName} />
                      {errors.apellidoMaterno && <p className="text-red-500 text-sm">{errors.apellidoMaterno.message}</p>}
                    </div>
                    {isExtranjero && (
                      <div className="space-y-2">
                        <Label className="text-sm font-bold text-gray-700">Edad *</Label>
                        <Input type="number" {...register("edad")} readOnly={lockVerifiedFields} className={lockedFieldClassName} />
                        {errors.edad && <p className="text-red-500 text-sm">{errors.edad.message}</p>}
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label className="text-sm font-bold text-gray-700">Género *</Label>
                      <Controller name="genero" control={control} render={({ field }) => (
                        <Select onValueChange={field.onChange} value={field.value || ""} disabled={lockVerifiedFields}>
                          <SelectTrigger className="bg-white"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mujer">Mujer</SelectItem>
                            <SelectItem value="hombre">Hombre</SelectItem>
                            <SelectItem value="no-binario">No binario</SelectItem>
                            <SelectItem value="prefiero-no-decir">Prefiero no decir</SelectItem>
                            <SelectItem value="otro">Otro</SelectItem>
                          </SelectContent>
                        </Select>
                      )} />
                      {errors.genero && <p className="text-red-500 text-sm">{errors.genero.message}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-bold text-gray-700">Correo Electrónico *</Label>
                      <Input type="email" {...register("correo")} />
                      {errors.correo && <p className="text-red-500 text-sm">{errors.correo.message}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-bold text-gray-700">Número de teléfono {selectedRole === 'SPEAKER' && '*'}</Label>
                      <Controller
                        name="telefono"
                        control={control}
                        render={({ field }) => (
                          <PhoneInput
                            countries={countries}
                            value={field.value || ""}
                            onChange={field.onChange}
                            defaultCountryIso={phoneCountryIso || selectedCountryIso}
                            onManualInteraction={() => setPhoneTouchedManually(true)}
                          />
                        )}
                      />
                      {errors.telefono && <p className="text-red-500 text-sm">{errors.telefono.message}</p>}
                    </div>
                  </div>
                </FormSection>
              )}

              {showPersonalSection && (
                <FormSection title="3. Necesidades especiales o consideraciones de salud" icon={User}>
                  <div className="space-y-6">
                    <div className="space-y-3">
                      <Label className="text-sm font-bold text-gray-700">
                        ¿Tienes alguna condición médica, restricción de movilidad o necesidad especial para el evento? *
                      </Label>
                      <Controller
                        name="healthData.conditions"
                        control={control}
                        render={() => (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {HEALTH_CONDITION_OPTIONS.map((condition) => {
                              const checked = healthConditions.includes(condition);
                              return (
                                <label
                                  key={condition}
                                  className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${checked ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                                    }`}
                                >
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={(nextChecked) => handleHealthConditionToggle(condition, Boolean(nextChecked))}
                                    className="mt-1"
                                  />
                                  <span className="text-sm text-gray-700">{condition}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      />
                      {healthErrors.conditions?.message && <p className="text-red-500 text-sm">{healthErrors.conditions.message as string}</p>}
                    </div>

                    {showHealthDetails && (
                      <div className="rounded-lg border border-rose-100 bg-rose-50/60 p-4 space-y-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-bold text-gray-700">Detalles importantes</Label>
                          <Textarea
                            {...register("healthData.detailsImportant" as any)}
                            placeholder="Comparte información relevante para la atención del evento."
                            className="min-h-[110px]"
                            maxLength={500}
                          />
                          {healthErrors.detailsImportant && <p className="text-red-500 text-sm">{healthErrors.detailsImportant.message as string}</p>}
                        </div>

                        {isOtherHealthConditionSelected && (
                          <div className="space-y-2">
                            <Label className="text-sm font-bold text-gray-700">Otra (especificar) *</Label>
                            <Textarea
                              {...register("healthData.otherDetails" as any)}
                              placeholder="Especifica claramente la necesidad especial."
                              className="min-h-[90px]"
                              maxLength={500}
                            />
                            {healthErrors.otherDetails && <p className="text-red-500 text-sm">{healthErrors.otherDetails.message as string}</p>}
                          </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-sm font-bold text-gray-700">Contacto de emergencia: nombre *</Label>
                            <Input {...register("healthData.emergencyContactName" as any)} />
                            {healthErrors.emergencyContactName && <p className="text-red-500 text-sm">{healthErrors.emergencyContactName.message as string}</p>}
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm font-bold text-gray-700">Contacto de emergencia: teléfono *</Label>
                            <Controller
                              name="healthData.emergencyContactPhone"
                              control={control}
                              render={({ field }) => (
                                <PhoneInput
                                  countries={countries}
                                  value={field.value || ""}
                                  onChange={field.onChange}
                                  defaultCountryIso={phoneCountryIso || selectedCountryIso}
                                  onManualInteraction={() => setPhoneTouchedManually(true)}
                                />
                              )}
                            />
                            {healthErrors.emergencyContactPhone && <p className="text-red-500 text-sm">{healthErrors.emergencyContactPhone.message as string}</p>}
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm font-bold text-gray-700">Tipo de sangre *</Label>
                            <Controller
                              name="healthData.bloodType"
                              control={control}
                              render={({ field }) => (
                                <Select onValueChange={field.onChange} value={field.value || ""}>
                                  <SelectTrigger className="bg-white">
                                    <SelectValue placeholder="Selecciona tu tipo de sangre" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {BLOOD_TYPE_OPTIONS.map((bloodType) => (
                                      <SelectItem key={bloodType} value={bloodType}>{bloodType}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            />
                            {healthErrors.bloodType && <p className="text-red-500 text-sm">{healthErrors.bloodType.message as string}</p>}
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm font-bold text-gray-700">¿Autorizas asistencia médica en caso necesario? *</Label>
                            <Controller
                              name="healthData.medicalAuthorization"
                              control={control}
                              render={({ field }) => (
                                <RadioGroup
                                  value={
                                    typeof field.value === "boolean"
                                      ? field.value ? "si" : "no"
                                      : ""
                                  }
                                  onValueChange={(value) => field.onChange(value === "si")}
                                  className="grid grid-cols-1 md:grid-cols-2 gap-3"
                                >
                                  <label className={`flex items-center gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${field.value === true ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                                    }`}>
                                    <RadioGroupItem value="si" id="medical-authorization-yes" />
                                    <span className="text-sm text-gray-700">Sí, autorizo asistencia médica</span>
                                  </label>
                                  <label className={`flex items-center gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${field.value === false ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                                    }`}>
                                    <RadioGroupItem value="no" id="medical-authorization-no" />
                                    <span className="text-sm text-gray-700">No autorizo asistencia médica</span>
                                  </label>
                                </RadioGroup>
                              )}
                            />
                            {healthErrors.medicalAuthorization && <p className="text-red-500 text-sm">{healthErrors.medicalAuthorization.message as string}</p>}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </FormSection>
              )}

              {showPersonalSection && (
                <FormSection title="4. Origen" icon={MapPin}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-bold text-gray-700">País *</Label>
                      <Controller
                        name="pais"
                        control={control}
                        render={({ field }) => (
                          <Select
                            onValueChange={(val) => {
                              field.onChange(val);
                              const selectedCountry = countries.find((country) => country.name === val);
                              const selectedIso = selectedCountry?.iso2 || "";
                              setSelectedCountryIso(selectedIso);
                              if (!phoneTouchedManually) {
                                setPhoneCountryIso(selectedIso);
                              }
                              setValue("estado", "");
                              setValue("ciudad", "");
                              setSelectedStateIso("");
                            }}
                            value={field.value || ""}
                          >
                            <SelectTrigger className="bg-white"><SelectValue placeholder="Selecciona un país" /></SelectTrigger>
                            <SelectContent className="max-h-[300px]">
                              {countries.map((country, index) => (
                                <SelectItem key={country.iso2 || `country-${index}`} value={country.name}>{country.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {errors.pais && <p className="text-red-500 text-sm">{errors.pais.message}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-bold text-gray-700">Estado *</Label>
                      <Controller
                        name="estado"
                        control={control}
                        render={({ field }) => (
                          (states && states.length > 0) ? (
                            <Select
                              onValueChange={(val) => {
                                field.onChange(val);
                                const selectedState = states?.find((s: any) => s.name === val);
                                setSelectedStateIso((selectedState as any)?.iso2 || "");
                                setValue("ciudad", "");
                              }}
                              value={field.value || ""}
                              disabled={isLoadingStates || !selectedCountryIso}
                            >
                              <SelectTrigger className="bg-white"><SelectValue placeholder="Selecciona un estado" /></SelectTrigger>
                              <SelectContent className="max-h-[300px]">
                                {states?.map((state: any, index: number) => (
                                  <SelectItem key={(state as any).iso2 || `state-${index}`} value={(state as any).name}>{(state as any).name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input {...field} placeholder="Escribe el estado" disabled={!selectedCountryIso} />
                          )
                        )}
                      />
                      {errors.estado && <p className="text-red-500 text-sm">{errors.estado.message}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-bold text-gray-700">Ciudad *</Label>
                      {showManualCityInput ? (
                        <Input {...register("ciudad")} placeholder="Escribe la ciudad" />
                      ) : (
                        <Controller
                          name="ciudad"
                          control={control}
                          render={({ field }) => (
                            cities && cities.length > 0 ? (
                              <Select
                                onValueChange={field.onChange}
                                value={field.value || ""}
                                disabled={isLoadingCities || !selectedStateIso}
                              >
                                <SelectTrigger className="bg-white"><SelectValue placeholder="Selecciona una ciudad" /></SelectTrigger>
                                <SelectContent className="max-h-[300px]">
                                  {cities.map((city: any) => (
                                    <SelectItem key={city.name} value={city.name}>{city.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input {...field} placeholder="Escribe la ciudad" disabled={!selectedCountryIso || !selectedStateIso} />
                            )
                          )}
                        />
                      )}
                      <div className="flex items-center space-x-2 mt-2">
                        <input type="checkbox" id="manualCity" checked={showManualCityInput} onChange={(e) => {
                          setShowManualCityInput(e.target.checked);
                          setValue("ciudad", "");
                        }} className="h-4 w-4 accent-blue-600" />
                        <Label htmlFor="manualCity" className="text-sm font-normal">Mi ciudad no aparece en la lista</Label>
                      </div>
                      {errors.ciudad && <p className="text-red-500 text-sm">{errors.ciudad.message}</p>}
                    </div>
                  </div>
                </FormSection>
              )}

              {showPersonalSection && (
                <FormSection title="5. Datos Académicos" icon={Building}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2 md:col-span-2">
                      <Label className="text-sm font-bold text-gray-700">Institución de Procedencia *</Label>
                      <Input {...register("institucion")} />
                      {errors.institucion && <p className="text-red-500 text-sm">{errors.institucion.message}</p>}
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label className="text-sm font-bold text-gray-700">Tipo de Participante *</Label>
                      <Controller name="tipoParticipante" control={control} render={({ field }) => (
                        <Select onValueChange={field.onChange} value={field.value || ""} disabled={selectedRole === 'ATTENDEE' && (isUnachStudent === true || isSpecialGeneralAccessSelectedEvent)}>
                          <SelectTrigger className={selectedRole === 'ATTENDEE' && (isUnachStudent === true || isSpecialGeneralAccessSelectedEvent) ? "bg-gray-100 text-gray-500 border-gray-200" : "bg-white"}>
                            <SelectValue placeholder="Selecciona tu tipo..." />
                          </SelectTrigger>
                          <SelectContent>
                            {(selectedRole === 'ATTENDEE' ? TIPOS_ASISTENTE : TIPOS_PONENTE).map(type => (
                              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )} />
                      {selectedRole === 'ATTENDEE' && isUnachStudent === true && (
                        <p className="text-xs text-slate-600">
                          Se fijó automáticamente en <strong>Estudiante UNACH</strong> porque activaste la validación institucional.
                        </p>
                      )}
                      {selectedRole === 'ATTENDEE' && isSpecialGeneralAccessSelectedEvent && (
                        <p className="text-xs text-slate-600">
                          Este evento especial etiqueta el registro automáticamente como <strong>Estudiante UNACH</strong> si validas matrícula; en caso contrario como <strong>Público General</strong>.
                        </p>
                      )}
                      {errors.tipoParticipante && <p className="text-red-500 text-sm">{errors.tipoParticipante.message}</p>}
                    </div>

                    {selectedRole === 'SPEAKER' && (
                      <div className="space-y-2">
                        <Label className="text-sm font-bold text-gray-700">Área de Adscripción *</Label>
                        <Input {...register("area" as any)} placeholder="Ej. Facultad de Ingeniería" />
                        {(errors as any).area && <p className="text-red-500 text-sm">{(errors as any).area.message}</p>}
                      </div>
                    )}
                  </div>
                </FormSection>
              )}

              {showPersonalSection && selectedRole === 'SPEAKER' && (
                <>
                  <FormSection title="6. Datos de la Ponencia" icon={Presentation}>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-bold text-gray-700">Título de la Contribución *</Label>
                        <Input {...register("tituloContribucion" as any)} placeholder="Máximo 25 palabras" />
                        {(errors as any).tituloContribucion && <p className="text-red-500 text-sm">{(errors as any).tituloContribucion.message}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-bold text-gray-700">Resumen (Abstract) *</Label>
                        <Textarea {...register("resumen" as any)} className="min-h-[150px]" placeholder="Entre 50 y 2000 caracteres" />
                        {(errors as any).resumen && <p className="text-red-500 text-sm">{(errors as any).resumen.message}</p>}
                      </div>
                    </div>
                  </FormSection>

                  <FormSection title="7. Coautores (Máximo 3)" icon={Users}>
                    <div className="space-y-4">
                      {fields.map((item, index) => (
                        <div key={item.id} className="p-4 border rounded-lg space-y-3 relative bg-gray-50">
                          <div className="absolute top-2 right-2">
                            <Button type="button" variant="ghost" size="icon" onClick={() => remove(index)} className="text-red-500 hover:text-red-700">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          <h4 className="font-medium text-sm text-gray-700">Coautor {index + 1}</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Input {...register(`coautores.${index}.nombre` as any)} placeholder="Nombre completo" />
                            <Input {...register(`coautores.${index}.correo` as any)} placeholder="Correo electrónico" />
                            <Input {...register(`coautores.${index}.institucion` as any)} placeholder="Institución" className="md:col-span-2" />
                          </div>
                        </div>
                      ))}
                      {fields.length < 3 && (
                        <Button type="button" variant="outline" onClick={() => append({ nombre: '', correo: '', institucion: '' })} className="w-full border-dashed">
                          <PlusCircle className="h-4 w-4 mr-2" /> Agregar Coautor
                        </Button>
                      )}
                    </div>
                  </FormSection>

                  <FormSection title="8. Documento Extenso" icon={FileArchive}>
                    <div className="space-y-2">
                      <Label className="text-sm font-bold text-gray-700">Ponencia en Extenso (.doc, .docx) *</Label>
                      <Input type="file" {...register("documento" as any)} accept=".doc,.docx" />
                      <p className="text-xs text-gray-500">Formato editable (Word) requerido para revisión.</p>
                      {(errors as any).documento && <p className="text-red-500 text-sm">{(errors as any).documento.message}</p>}
                    </div>
                  </FormSection>
                </>
              )}

              {showPersonalSection && (
                <FormSection title={otherSectionTitle} icon={Globe}>
                  <div className="space-y-2">
                    <Label className="text-sm font-bold text-gray-700">¿Cómo te enteraste del evento?</Label>
                    <Controller name="comoSeEntero" control={control} render={({ field }) => (
                      <Select onValueChange={field.onChange} value={field.value || ""}>
                        <SelectTrigger className="bg-white"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pagina-oficial">Página oficial</SelectItem>
                          <SelectItem value="redes-sociales">Redes Sociales</SelectItem>
                          <SelectItem value="correo">Correo Institucional</SelectItem>
                          <SelectItem value="otro">Otro</SelectItem>
                        </SelectContent>
                      </Select>
                    )} />
                    {errors.comoSeEntero && <p className="text-red-500 text-sm">{errors.comoSeEntero.message}</p>}
                  </div>

                  <div className="flex items-start space-x-3 pt-4">
                    <Controller name="aceptaTerminos" control={control} render={({ field }) => {
                      const isAccepted = Boolean(field.value);

                      return (
                        <>
                          <input
                            type="checkbox"
                            id="aceptaTerminos"
                            checked={isAccepted}
                            onClick={(event) => {
                              if (!isAccepted) {
                                event.preventDefault();
                                setIsDeclaratoriaOpen(true);
                                return;
                              }
                              field.onChange(false);
                            }}
                            onChange={(event) => {
                              event.preventDefault();
                            }}
                            className="h-4 w-4 mt-1 accent-blue-600"
                          />
                          <DeclaratoriaModal
                            open={isDeclaratoriaOpen}
                            onAccept={() => {
                              field.onChange(true);
                              setIsDeclaratoriaOpen(false);
                            }}
                            onCancel={() => {
                              field.onChange(false);
                              setIsDeclaratoriaOpen(false);
                            }}
                          />
                        </>
                      );
                    }} />
                    <Label htmlFor="aceptaTerminos" className="text-sm leading-5">Acepto los términos y condiciones del congreso, así como el aviso de privacidad de la Universidad. *</Label>
                  </div>
                  {errors.aceptaTerminos && <p className="text-red-500 text-sm">{errors.aceptaTerminos.message}</p>}
                </FormSection>
              )}

              {showPersonalSection && (
                <div className="text-center pt-8 pb-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting || !csrfToken}
                    className="bg-[#0f2744] hover:bg-[#142d4d] text-white px-16 py-8 text-xl w-full md:w-auto shadow-xl transition-all font-bold uppercase tracking-wider border-b-[6px] border-[#c9a227] active:border-b-0 active:translate-y-[2px] rounded-lg"
                  >
                    {isSubmitting ? "Procesando..." : "ENVIAR REGISTRO"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </form>
      </div>

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title="¡Registro Exitoso!"
        description={
          registrationData && (
            <div className="space-y-2 text-sm">
              <p>Hola <strong>{registrationData.nombre}</strong>, tu registro ha sido completado.</p>
              <div className="p-4 mt-4 bg-green-50 border border-green-200 rounded-lg text-center">
                <p className="text-xs text-green-700 uppercase font-semibold tracking-wide">Tu Folio de Participante</p>
                <p className="text-2xl font-mono font-bold text-green-800 my-1">{registrationData.folio}</p>
                <p className="text-xs text-green-600">Conserva este folio para futuras consultas.</p>
              </div>
              <p className="mt-4 text-center text-gray-500">Se ha enviado una confirmación a tu correo.</p>
            </div>
          )
        }
      />

      <AlertDialog open={showScientificCurpBlock} onOpenChange={setShowScientificCurpBlock}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Registro ya cubierto por eje Científico</AlertDialogTitle>
            <AlertDialogDescription>
              Ya cuentas con un registro activo en el eje Científico. Si ya realizaste el pago de la cuota de recuperación, tu QR de acceso es válido también para eventos Culturales y Empresariales.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => {
                setShowScientificCurpBlock(false);
                window.location.assign(import.meta.env.BASE_URL || '/');
              }}
            >
              Volver al inicio
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showUnachSwitchConfirm} onOpenChange={setShowUnachSwitchConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cambiar fuente de datos</AlertDialogTitle>
            <AlertDialogDescription>
              Actualmente los datos están validados con matrícula UNACH (SIAE). Si continúas con "No",
              se eliminará la validación SIAE y se restaurarán los datos validados con CURP.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmUnachNo}>Confirmar cambio</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}


